import React from "react";

const baseText = {
  margin: "6px 0",
  color: "#1b2430",
  fontSize: "15px",
  lineHeight: "1.45",
};

const label = { fontWeight: 700, color: "#101828" };
const orange = "#ed5d1a";
const red = "#d92323";
const gray = "#5b6675";
const headingFont = "'Montserrat', sans-serif";
const bodyFont = "'Barlow', sans-serif";

const formatDateValue = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatInvoiceDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatInvoiceNumber = (primary, fallback) => {
  const direct = String(primary || "").trim();
  if (/^A\d{3,}$/i.test(direct)) return direct.toUpperCase();

  let numeric = null;
  if (/^\d+$/.test(direct)) numeric = Number(direct);
  if (!Number.isFinite(numeric)) {
    const digits = String(primary || fallback || "").replace(/\D/g, "");
    if (digits) numeric = Number(digits.slice(-3));
  }
  if (!Number.isFinite(numeric) || numeric <= 0) numeric = 1;

  return `A${String(numeric).padStart(3, "0")}`;
};

const pageStyle = `
  @page {
    size: 1000px 700px;
    margin: 0;
  }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    width: 1000px;
    height: 700px;
    background: #ffffff;
  }
`;

const ConsultantInvoiceTemplate = React.forwardRef(({ data }, ref) => {
  if (!data) return null;
  const invoiceDate = data.invoiceDate || data.date;
  const invoiceNumber = formatInvoiceNumber(
    data.invoice_no || data.invoiceNumber || data.invoice_id || data.sequence_no,
    data.booking_id
  );

  return (
    <>
      <style>{pageStyle}</style>
      <div
        ref={ref}
        className="consultant-invoice-container"
        style={{
          width: "1000px",
          minHeight: "700px",
          height: "700px",
          background: "#ffffff",
          padding: "14px 8px 10px",
          margin: "0",
          boxSizing: "border-box",
          fontFamily: bodyFont,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <h2
            style={{
              margin: "-8px 0 6px 0",
              textAlign: "center",
              color: red,
              fontSize: "44px",
              fontWeight: 800,
              whiteSpace: "nowrap",
              lineHeight: 1,
              fontFamily: headingFont,
            }}
          >
            YOUR SLOT IS BOOKED
          </h2>

          <div style={{ display: "grid", gridTemplateColumns: "190px 1fr 210px", alignItems: "start", gap: "6px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <img
                src="/image/logo.png"
                alt="MarsMate AI Logo"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
                style={{ width: "205px", height: "88px", objectFit: "contain", objectPosition: "left center" }}
              />
            </div>
            <div />

            <div style={{ textAlign: "right" }}>
              <h1
                style={{
                  margin: "0",
                  color: "#3f4f66",
                  fontSize: "38px",
                  lineHeight: 1,
                  fontFamily: headingFont,
                }}
              >
                INVOICE
              </h1>
              <p style={{ ...baseText, margin: "8px 0 0 0", color: gray, fontSize: "14px" }}>
                Invoice No: {invoiceNumber}
              </p>
              <p style={{ ...baseText, margin: "2px 0 0 0", color: gray, fontSize: "15px" }}>
                Date: {formatInvoiceDate(invoiceDate)}
              </p>
            </div>
          </div>

          <div style={{ borderBottom: `3px solid ${orange}`, margin: "10px 0 14px 0" }} />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "24px",
            }}
          >
            <div>
              <h3 style={{ color: orange, fontSize: "16px", margin: 0, fontFamily: headingFont }}>BILLED TO</h3>
              <div style={{ borderTop: "1px solid #ddd", margin: "8px 0 10px 0" }} />
              <p style={{ ...baseText, fontSize: "15px" }}>
                <span style={label}>Name:</span> {data.name}
              </p>
              <p style={{ ...baseText, fontSize: "15px" }}>
                <span style={label}>Address:</span> {data.address}
              </p>
              <p style={{ ...baseText, fontSize: "15px" }}>
                <span style={label}>Email:</span> {data.email}
              </p>
              <p style={{ ...baseText, fontSize: "15px" }}>
                <span style={label}>Phone:</span> {data.phone}
              </p>
            </div>

            <div>
              <h3 style={{ color: orange, fontSize: "16px", margin: 0, fontFamily: headingFont }}>CONSULTATION DETAILS</h3>
              <div style={{ borderTop: "1px solid #ddd", margin: "8px 0 10px 0" }} />
              <p style={{ ...baseText, fontSize: "15px" }}>
                <span style={label}>Topic:</span> {data.topic}
              </p>
              <p style={{ ...baseText, fontSize: "15px" }}>
                <span style={label}>Mode:</span> {data.mode}
              </p>
              <p style={{ ...baseText, fontSize: "15px" }}>
                <span style={label}>Scheduled Date:</span> {formatDateValue(data.date)}
              </p>
              <p style={{ ...baseText, fontSize: "15px" }}>
                <span style={label}>Scheduled Time:</span> {data.time}
              </p>
            </div>
          </div>

          <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", flex: 1, minHeight: "260px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 180px",
                background: orange,
              }}
            >
              <div
                style={{
                  padding: "12px 14px",
                  color: "#fff",
                  textAlign: "left",
                  fontSize: "16px",
                  fontFamily: headingFont,
                  borderRight: "1px solid rgba(255,255,255,0.45)",
                }}
              >
                Description
              </div>
              <div
                style={{
                  padding: "12px 14px",
                  color: "#fff",
                  textAlign: "right",
                  fontSize: "16px",
                  fontFamily: headingFont,
                }}
              >
                Amount
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 180px", alignItems: "start", flex: 1, minHeight: "230px" }}>
              <div style={{ padding: "14px 12px 28px 12px" }}>
                <p style={{ ...baseText, fontWeight: 700, fontSize: "17px" }}>
                  Professional Consultation Service
                </p>
                <p style={{ ...baseText, marginTop: "8px", fontSize: "15px", color: "#444f5e", whiteSpace: "pre-wrap" }}>
                  <b>Why do you want to meet / Consult:</b> {data.notes || "-"}
                </p>
              </div>
              <div style={{ padding: "14px 12px 28px 12px", textAlign: "right" }}>
                <p style={{ ...baseText, marginTop: "8px", fontSize: "17px", fontWeight: 700 }}>Rs. 199.00</p>
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginTop: "auto", paddingTop: "6px" }}>
          <div style={{ borderBottom: "1px solid #d7d7d7", marginBottom: "12px", width: "100%" }} />
          <p style={{ textAlign: "center", color: "#8f9aa8", fontSize: "14px", fontWeight: 700, margin: "0 0 8px 0" }}>
            Thank you for your business.
          </p>
          <p style={{ textAlign: "center", color: "#8f9aa8", fontSize: "13px", lineHeight: "1.35", fontWeight: 600, margin: "4px 0 0 0" }}>
            If you have any questions about this invoice, please contact support@marsmate.com
          </p>
        </div>
      </div>
    </>
  );
});

export default ConsultantInvoiceTemplate;
